import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@amore/ui/components/tooltip'
import { useLocation, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  ArrowRight,
  Baby,
  BedDouble,
  Car,
  Link2,
  ListPlus,
  type LucideIcon,
  MessageSquareText,
  Plus,
  Salad,
  Send,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { plural } from '@/lib/format'
import { dietLabels } from '@/lib/labels'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Party, WeddingData } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { FilterBar } from '@/patterns/filter-bar'
import { PromptList, useAddPromptTask } from '@/patterns/prompts'
import { Screen } from '@/patterns/screen'
import { SectionTabs } from '@/patterns/section-tabs'
import { StatSummary } from '@/patterns/stat-summary'
import guestsIllustration from './assets/guests.svg'
import { AttendanceDots, partyDots } from './attendance'
import { BulkDialog } from './bulk-dialog'
import {
  circleGroupLabels,
  circleOrder,
  filterLabels,
  type GuestFilter,
  type GuestTab,
  guestFilters,
  guestTotals,
  hasNewReply,
  invitationLabel,
  matchesFilter,
  matchesSearch,
  parseFilter,
  partyCounts,
  partyPeople,
  partyTitle,
} from './model'
import { OrganizationPage } from './organization'
import { PartySheet } from './party-sheet'
import { type GuestPromptAction, guestPrompts } from './prompts'
import { RepliesPage } from './replies'
import { ReviewSheet } from './review-sheet'
import { ShareSheet } from './share-sheet'

const tabPaths: Record<GuestTab, string> = {
  list: '/guests',
  replies: '/guests/replies',
  organization: '/guests/settings',
}

export function GuestsScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { partyId } = useParams({ strict: false }) as { partyId?: string }
  const [adding, setAdding] = useState(false)
  const [pasting, setPasting] = useState(false)
  const [sharing, setSharing] = useState(false)
  const openParty = data.parties.find((party) => party.id === partyId)
  const add = () => setAdding(true)
  const tab: GuestTab = pathname.startsWith(tabPaths.replies)
    ? 'replies'
    : pathname.startsWith(tabPaths.organization)
      ? 'organization'
      : 'list'
  const { replyId } = useParams({ strict: false }) as { replyId?: string }
  const openReply = data.webReplies.find((reply) => reply.id === replyId)
  const totals = guestTotals(data)
  const empty = data.parties.length === 0

  return (
    <Screen
      title="Hosté"
      illustration={<img src={guestsIllustration} alt="" className="h-auto w-full" />}
      description="Kdo je pozvaný, kdo přijde a co potřebuje."
      actions={
        canEdit && !empty && tab === 'list' ? (
          <>
            <Button appearance="outline" tone="neutral" onClick={() => setPasting(true)}>
              <ListPlus aria-hidden="true" className="size-4" />
              Vložit seznam
            </Button>
            <Button onClick={add}>
              <Plus aria-hidden="true" className="size-4" />
              Přidat pozvánku
            </Button>
          </>
        ) : null
      }
      tabs={
        <SectionTabs
          label="Sekce hostů"
          active={tabPaths[tab]}
          tabs={[
            { to: tabPaths.list, label: 'Seznam hostů' },
            {
              to: tabPaths.replies,
              label: 'Odpovědi z webu',
              count: totals.newReplies,
            },
            { to: tabPaths.organization, label: 'Nastavení' },
          ]}
          action={
            <Button appearance="outline" tone="neutral" size="sm" onClick={() => setSharing(true)}>
              <Link2 aria-hidden="true" className="size-4" />
              Svatební odkaz
            </Button>
          }
        />
      }
    >
      {tab === 'list' ? (
        empty ? (
          <EmptyState
            icon={<img src={guestsIllustration} alt="" className="h-28 w-32" />}
            title="Zatím tu nejsou žádní hosté"
            description="Vložte celý seznam jmen najednou, nebo přidávejte pozvánky postupně. Jedna pozvánka je jedna domácnost nebo pár."
            actions={
              canEdit ? (
                <>
                  <Button onClick={() => setPasting(true)}>Vytvořit seznam hostů</Button>
                  <Button appearance="outline" tone="neutral" onClick={add}>
                    Přidat pozvánku
                  </Button>
                </>
              ) : null
            }
          />
        ) : (
          <GuestList
            data={data}
            onOpen={(party) => void navigate({ to: `/guests/${party.id}`, search })}
            onShare={() => setSharing(true)}
          />
        )
      ) : tab === 'replies' ? (
        <RepliesPage
          data={data}
          onShare={() => setSharing(true)}
          onOpen={(reply) => void navigate({ to: `/guests/replies/${reply.id}` })}
        />
      ) : (
        <OrganizationPage />
      )}

      {openParty ? (
        <PartySheet
          key={openParty.id}
          party={openParty}
          onClose={() => void navigate({ to: '/guests', search })}
        />
      ) : null}
      {adding ? <PartySheet onClose={() => setAdding(false)} /> : null}
      {pasting ? <BulkDialog onClose={() => setPasting(false)} /> : null}
      {openReply ? (
        <ReviewSheet
          key={openReply.id}
          reply={openReply}
          onClose={() => void navigate({ to: '/guests/replies' })}
        />
      ) : null}
      {sharing ? <ShareSheet onClose={() => setSharing(false)} /> : null}
    </Screen>
  )
}

function GuestList({
  data,
  onOpen,
  onShare,
}: {
  data: WeddingData
  onOpen: (party: Party) => void
  onShare: () => void
}) {
  const navigate = useNavigate()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const addTask = useAddPromptTask()
  const search = useSearch({ strict: false }) as { filter?: string; q?: string }
  const filter = parseFilter(search.filter)
  const query = search.q ?? ''
  const totals = guestTotals(data)
  const replied = totals.confirmed + totals.declined
  const percent = totals.total ? Math.round((replied / totals.total) * 100) : 0

  const setSearch = (next: { filter?: GuestFilter; q?: string }) => {
    const merged = { filter, q: query, ...next }
    void navigate({
      to: '/guests',
      search: {
        ...(merged.filter !== 'all' ? { filter: merged.filter } : {}),
        ...(merged.q ? { q: merged.q } : {}),
      },
      replace: true,
    })
  }
  const toggleFilter = (value: GuestFilter) =>
    setSearch({ filter: filter === value ? 'all' : value })

  const searched = data.parties.filter((party) => matchesSearch(party, query))
  const visible = searched.filter((party) => matchesFilter(party, filter))
  const groups: TableGroup<Party>[] = circleOrder.flatMap((circle) => {
    const rows = visible.filter((party) => party.circle === circle)
    if (rows.length === 0) return []
    const people = rows.reduce((sum, party) => sum + party.persons.length, 0)
    return [
      {
        id: circle,
        label: circleGroupLabels[circle],
        meta: invitations(rows.length),
        summary: { people },
        rows,
        collapsible: true,
      },
    ]
  })

  return (
    <>
      <StatSummary
        groups={[
          {
            title: 'Odpovědi',
            value: `${percent} %`,
            progress: {
              value: replied,
              max: totals.total,
              tone: 'success',
              label: `Odpovědělo ${replied} z ${totals.total} hostů`,
            },
            tiles: [
              {
                label: 'Přijdou',
                value: totals.confirmed,
                caption: `z ${totals.total} ${guests(totals.total)}`,
                tone: 'success',
                onSelect: () => toggleFilter('confirmed'),
                active: filter === 'confirmed',
              },
              {
                label: 'Nepřijdou',
                value: totals.declined,
                onSelect: () => toggleFilter('declined'),
                active: filter === 'declined',
              },
            ],
          },
          {
            title: 'Čeká se',
            tiles: [
              {
                label: 'Bez odpovědi',
                value: totals.pending,
                caption: `${invitations(totals.pendingParties)}`,
                onSelect: () => toggleFilter('pending'),
                active: filter === 'pending',
              },
              {
                label: 'Nové odpovědi z webu',
                value: totals.newReplies,
                caption: totals.newReplies ? 'Ke kontrole' : undefined,
                tone: totals.newReplies ? 'warning' : 'default',
                onSelect: () => void navigate({ to: '/guests/replies' }),
              },
            ],
          },
        ]}
      />

      {canEdit ? (
        <PromptList
          prompts={guestPrompts(data).map((prompt) => ({
            ...prompt,
            action: (
              <PromptAction
                action={prompt.action}
                onShare={onShare}
                onSettings={() => void navigate({ to: '/guests/settings' })}
                onTask={(title) =>
                  addTask(title, { labels: ['Hosté'], context: { type: 'guests' } })
                }
              />
            ),
          }))}
          onHide={(code) =>
            update((current) => ({ ...current, hiddenPrompts: [...current.hiddenPrompts, code] }))
          }
        />
      ) : null}

      <FilterBar
        filters={guestFilters.map((value) => ({
          value,
          label: filterLabels[value],
        }))}
        value={filter}
        onChange={(value) => setSearch({ filter: value })}
        search={{
          value: query,
          onChange: (value) => setSearch({ q: value }),
          placeholder: 'Hledat hosta',
        }}
      />

      <DataTable
        aria-label="Seznam hostů podle pozvánek"
        columns={guestColumns(data)}
        getRowId={(party) => party.id}
        onRowClick={onOpen}
        rowMuted={(party) => partyCounts(party).declined === party.persons.length}
        groups={groups}
        empty={
          <div className="space-y-3">
            <p className="font-semibold text-foreground">Nikoho takového nenacházíme</p>
            <p className="text-sm">Zkuste hledat jinak nebo zobrazit všechny hosty.</p>
            <Button
              appearance="outline"
              tone="neutral"
              size="sm"
              onClick={() => setSearch({ filter: 'all', q: '' })}
            >
              Zobrazit všechny hosty
            </Button>
          </div>
        }
      />
    </>
  )
}

function PromptAction({
  action,
  onShare,
  onSettings,
  onTask,
}: {
  action: GuestPromptAction
  onShare: () => void
  onSettings: () => void
  onTask: (title: string) => void
}) {
  const props = { size: 'sm', appearance: 'outline', tone: 'neutral' } as const
  if (action.kind === 'share')
    return (
      <Button {...props} onClick={onShare}>
        <Send aria-hidden="true" className="size-3.5" />
        Poslat připomínku
      </Button>
    )
  if (action.kind === 'settings')
    return (
      <Button {...props} onClick={onSettings}>
        {action.label}
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </Button>
    )
  return (
    <Button {...props} onClick={() => onTask(action.title)}>
      <Plus aria-hidden="true" className="size-3.5" />
      Přidat úkol
    </Button>
  )
}

const invitations = (count: number) =>
  `${count} ${plural(count, 'pozvánka', 'pozvánky', 'pozvánek')}`
const guests = (count: number) => plural(count, 'hosta', 'hostů', 'hostů')

function guestColumns(data: WeddingData): Column<Party>[] {
  return [
    {
      key: 'party',
      header: 'Pozvánka',
      width: 'minmax(0,1fr)',
      primary: true,
      cell: (party) => (
        <span className="block min-w-0">
          <span className="line-clamp-2">
            {partyTitle(party)}
            {hasNewReply(party, data) ? (
              <Badge tone="warning" className="ml-2 py-0.5 align-[1px]">
                Nová odpověď
              </Badge>
            ) : null}
          </span>
          {party.name ? (
            <span className="block truncate text-xs font-normal text-muted-foreground">
              {partyPeople(party)}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'attendance',
      header: 'Účast',
      width: '9rem',
      interactive: true,
      cell: (party) => <AttendanceDots people={partyDots(party.persons)} />,
    },
    {
      key: 'invitation',
      header: 'Pozvání',
      width: '10rem',
      mobile: 'hide',
      cell: (party) => invitationLabel(party.parts, data.dayParts),
    },
    {
      key: 'needs',
      header: 'Požadavky',
      width: '8rem',
      interactive: true,
      cell: (party) => <Needs party={party} />,
    },
    {
      key: 'people',
      header: 'Hostů',
      width: '4rem',
      align: 'end',
      numeric: true,
      mobile: 'hide',
      cell: (party) => party.persons.length,
    },
  ]
}

/** What the invitation needs, as quiet icons; the tooltip says it in words. */
function Needs({ party }: { party: Party }) {
  const diets = [...new Set(party.persons.flatMap((person) => person.diets))]
  const children = party.persons.filter((person) => person.child).length
  const items: { icon: LucideIcon; label: string }[] = [
    ...(diets.length
      ? [{ icon: Salad, label: diets.map((diet) => dietLabels[diet]).join(', ') }]
      : []),
    ...(children
      ? [{ icon: Baby, label: `${children} ${plural(children, 'dítě', 'děti', 'dětí')}` }]
      : []),
    ...(party.accommodation ? [{ icon: BedDouble, label: 'Ubytování' }] : []),
    ...(party.transport ? [{ icon: Car, label: 'Odvoz' }] : []),
    ...(party.message ? [{ icon: MessageSquareText, label: `„${party.message}“` }] : []),
  ]
  if (items.length === 0) return null
  return (
    <span className="flex items-center gap-1">
      {items.map((item) => (
        <NeedIcon key={item.label} {...item} />
      ))}
    </span>
  )
}

function NeedIcon({ icon: Icon, label }: { icon: LucideIcon; label: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          // biome-ignore lint/a11y/noNoninteractiveTabindex: the tooltip is the only way to read the icon
          tabIndex={0}
          className="grid size-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <Icon aria-hidden="true" className="size-4" />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-64">{label}</TooltipContent>
    </Tooltip>
  )
}
